import React from "react";
import styles from "./TeaserCard.module.scss";
import {
  MediaFormatType,
  TeaserSliderOptionsType,
} from "@/types/ModuleOptionsType";
import { ExtendedLinkType } from "../ExtededLink/ExtendedLink";
import { NormalFramelessFrameType } from "@/types/LayoutType";
import { FileType, ImageType, LinkType } from "../../types/baseTypes";
import { ContentTag } from "../ContentTag/ContentTag";
import { MediaItemResponsive } from "../MediaItem/MediaItemResponsive";
import { CustomLink } from "../CustomLink";
import { SVGIcon } from "../SVGIcon/SVGIcon";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Button } from "../Button/Button";
import { downloadFile } from "@/utils/downloadFile";
import useWindowSize, {
  DESKTOP_WIDTH,
  FULLHD_WIDTH,
} from "@/hooks/useWindowSize";
import { LineChart } from "../LineChart/LineChart";

export type TeaserSliderElementType = {
  title: string;
  minMax?: { min: number | ""; max: number | "" };
  name: string;
  content: string;
  image: ImageType | false;
  link: LinkType;
  link_extended?: ExtendedLinkType;
  pdfFactsheet: FileType | false;
};

interface TeaserCardProps {
  teaserSliderOptions: TeaserSliderOptionsType;
  element: TeaserSliderElementType;
  type: "multiselect" | "text-and-image" | "image";
  mediaFormat: MediaFormatType;
  moduleWidth: NormalFramelessFrameType;
  heading_level: number;
  metalCsvFile: FileType;
}

export const TeaserCard = ({
  teaserSliderOptions,
  element,
  moduleWidth,
  mediaFormat,
  type,
  heading_level,
  metalCsvFile,
}: TeaserCardProps) => {
  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    card_width,
  } = teaserSliderOptions;

  const { image, title, content, link, pdfFactsheet } = element;
  const { getIcon } = useGlobalContext();
  const { width } = useWindowSize();

  const displayLinkButtonInImage =
    width < DESKTOP_WIDTH || width >= FULLHD_WIDTH;
  const displayLinkButtonInText =
    width >= DESKTOP_WIDTH && width < FULLHD_WIDTH;

  const teaserCardStyle: React.CSSProperties = {
    "--card-width-tablet":
      (card_width &&
        card_width.find((x) => x.breakpoint === "tablet")?.number_of_cols) ||
      3,
    "--card-width-desktop":
      (card_width &&
        card_width.find((x) => x.breakpoint === "desktop")?.number_of_cols) ||
      3,
    "--card-width-fullhd":
      (card_width &&
        card_width.find((x) => x.breakpoint === "fullhd")?.number_of_cols) ||
      3,
  } as React.CSSProperties;
  const { getLabel } = useGlobalContext();
  const ExtendLinkJSX = pdfFactsheet && (
    <>
      <Button
        type="secondary"
        onClick={(event) => {
          if (!link) return;
          event.stopPropagation();
          event.preventDefault();
          downloadFile(pdfFactsheet.url).catch((e) => console.error(e));
        }}
        href=""
        target=""
        animation="none"
        className={styles["download-btn"]}
        isDownload
        aria-label={getLabel("aria_label_factsheet_btn").replace(
          "$title",
          title,
        )}
      >
        {getLabel("download_label")} {getLabel("factsheet_label")}
      </Button>

      <LineChart
        className={styles["chart-wrapper"]}
        url={metalCsvFile.url}
        name={element.name}
        min={element.minMax?.min || undefined}
        max={element.minMax?.max || undefined}
      />
    </>
  );

  return (
    <article
      data-module-width={moduleWidth}
      data-box-shadow={show_card_box && show_box_shadow}
      data-border-radius={show_card_box && show_border_radius}
      className={`${styles["teaser-card"]}`}
      style={teaserCardStyle}
    >
      <CustomLink
        type="secondary"
        href={element.link ? element.link.url : "/"}
        target="_self"
        className={styles["link-wrapper"]}
      >
        <div
          className={`${styles["image-wrapper"]} ${element.link ? styles["inverted-radius"] : ""}`}
        >
          {image && (
            <>
              <MediaItemResponsive
                hasOverlay={false}
                image_desktop={image}
                mediaFormat={mediaFormat}
                sizes={
                  "(max-width: 767px) 50vw, (max-width: 1023px) 50vw, (max-width: 1407px) 33vw, 512px"
                }
              />

              {displayLinkButtonInImage && (
                <div
                  className={`mb-0 ${styles["link-btn"]} link-btn`}
                  aria-hidden
                >
                  <SVGIcon
                    src={getIcon("ArrowBigRight")}
                    width={14}
                    height={14}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {type !== "image" && (
          <div
            className={`${styles["information-wrapper"]} ${element.link && displayLinkButtonInText ? styles["inverted-radius"] : ""}`}
          >
            {displayLinkButtonInText && (
              <div className={`mb-0 ${styles["link-btn"]}`} aria-hidden>
                <SVGIcon
                  src={getIcon("ArrowBigRight")}
                  width={14}
                  height={14}
                />
              </div>
            )}

            <header className={`${styles["text-wrapper"]} is-box`}>
              <h3 aria-level={heading_level} className={styles["title"]}>
                {title}
              </h3>

              {content && (
                <ContentTag extraClass={styles["content"]} content={content} />
              )}
            </header>
          </div>
        )}
      </CustomLink>

      <div className={`${styles["button-wrapper"]} is-box`}>
        {ExtendLinkJSX}
      </div>
    </article>
  );
};
